import { buildQueryObject, separateByComma } from "@utility/utilityFunctions";

const handleGroups = (groups) => {
  const categoryGroups = groups.reduce((categoryGroup, group) => {
    const id = group.groupCategoryId;
    categoryGroup[id] = categoryGroup[id] || [];
    categoryGroup[id].push(group);
    return categoryGroup;
  }, {});
  const groupArray = Object.keys(categoryGroups).map((key) => ({
    group_category_id: key,
    group_ids: categoryGroups[key].map((group) => group.id),
  }));
  return `filter[groups]=${JSON.stringify(groupArray)}`;
};

const handleOrderId = (filters) => {
  if (filters.filterType?.includes("order-history")) {
    return filters.orderId && filters.orderId.length > 0
      ? filters.groupOrderHistoryBy === "order"
        ? `filter[id]=${filters.orderId}`
        : `filter[order-id]=${filters.orderId}`
      : null;
  } else {
    return null;
  }
};

const handleStatus = (filters) => {
  if (filters.filterType?.includes("order-history")) {
    return filters.status && filters.status.length > 0
      ? filters.groupOrderHistoryBy === "order"
        ? separateByComma("status", filters.status.split(","))
        : separateByComma("order-status", filters.status.split(","))
      : null;
  } else if (filters.filterType === "purchase-order-history") {
    return filters.status && filters.status.length > 0
      ? separateByComma("status", filters.status.split(","))
      : null;
  } else if (
    filters.filterType === "reports-order-window-summary" ||
    filters.filterType === "reports-order-window-detail"
  ) {
    return filters.status && filters.status.length > 0
      ? separateByComma("statuses", filters.status.split(","))
      : null;
  } else if (filters.filterType === "reports-product-list-report") {
    return filters.status && filters.status !== "all"
      ? `filter[status]=${filters.status}`
      : null;
  } else {
    return null;
  }
};

const handleSubmittedAtRange = (filters) => {
  // const draftStatuses = ["draft", "inactive", "in-progress", "missed-payment"];

  if (
    filters.filterType?.includes("order-history")
    // !draftStatuses.includes(filters.status)
  ) {
    return filters.dateRangeStart &&
      filters.dateRangeEnd &&
      filters.status !== "draft" &&
      filters.groupOrderHistoryBy
      ? filters.groupOrderHistoryBy === "order"
        ? `filter[submitted-at-range]=${filters.dateRangeStart} - ${filters.dateRangeEnd}`
        : `filter[order-submitted-at-range]=${filters.dateRangeStart} - ${filters.dateRangeEnd}`
      : null;
  } else {
    return null;
  }
};

export const buildFilters = (filters, uniqueFilter, sortString, urlBase) => {
  const budgetGroupIds =
    filters.budgetGroupIds && filters.budgetGroupIds.length > 0
      ? separateByComma("group-ids", filters.budgetGroupIds, "id")
      : null;
  const budgetId = filters.budget
    ? `filter[budget-id]=${filters.budget.id}`
    : null;
  const budgetIsActive =
    filters.filterType === "budgets" ||
    filters.filterType === "reports-budget-summary-export-report"
      ? filters.budgetIsActive
        ? `filter[is-active]=true`
        : `filter[is-active]=false`
      : null;
  const channelId =
    filters.channelId &&
    filters.channelId.length > 0 &&
    !filters.ignoreUserChannel
      ? separateByComma("channel-ids", filters.channelId)
      : null;
  const channelIds =
    filters.channelIds && filters.channelIds.length > 0
      ? separateByComma("channel-ids", filters.channelIds, "id")
      : null;
  const currentTerritoryId =
    filters.currentTerritoryId &&
    filters.currentTerritoryId.length > 0 &&
    !filters.ignoreUserTerritory
      ? separateByComma("territory-ids", filters.currentTerritoryId)
      : null;
  const description =
    filters.description && filters.description.length > 0
      ? `filter[description]=${filters.description}`
      : null;
  const favoriteItems =
    filters.favoriteItems && filters.favoriteItems.length > 0
      ? separateByComma("ids", filters.favoriteItems, "id")
      : null;
  const groupIds =
    filters.groupIds && filters.groupIds.length > 0
      ? handleGroups(filters.groupIds)
      : null;
  const groupedBy =
    filters.groupedBy && filters.groupedBy.length > 0
      ? `filter[grouped-by]=${filters.groupedBy}`
      : null;
  const isActive =
    filters.isActive !== null && filters.isActive !== undefined
      ? `filter[is-active]=${filters.isActive}`
      : null;
  const isOrderable =
    filters.isOrderable !== null && filters.isOrderable !== undefined
      ? `filter[is-orderable]=${filters.isOrderable}`
      : null;
  const isVisible =
    filters.isVisible !== null && filters.isVisible !== undefined
      ? `filter[is-visible]=${filters.isVisible}`
      : null;
  const name =
    filters.name && filters.name.length > 0
      ? `filter[name]=${filters.name}`
      : null;
  const orderId = handleOrderId(filters);
  const orderHistoryType =
    filters.orderType &&
    ((filters.groupOrderHistoryBy &&
      filters.filterType?.includes("order-history")) ||
      filters.filterType === "reports-shipped-orders-report")
      ? filters.groupOrderHistoryBy === "order"
        ? `filter[type]=${filters.orderType}`
        : `filter[order-type]=${filters.orderType}`
      : null;
  const orderSubmittedRange = handleSubmittedAtRange(filters);
  const orderWindowId =
    filters.orderWindowId && filters.orderWindowId.length > 0
      ? `filter[order-window-id]=${filters.orderWindowId}`
      : null;
  const orderWindowIds =
    filters.filterType !== "reports-pre-order-user-report" &&
    filters.orderWindowIds &&
    filters.orderWindowIds.length > 0
      ? separateByComma("order-window-ids", filters.orderWindowIds, "id")
      : null;
  const orderWindowTerritoryId =
    filters.useOrderWindowTerritoryId &&
    filters.currentTerritoryId &&
    filters.currentTerritoryId.length > 0
      ? `filter[order-window-territory-id]=${filters.currentTerritoryId}`
      : null;
  const preOrderWindowId =
    filters.filterType === "reports-pre-order-user-report" &&
    filters.orderWindowIds &&
    filters.orderWindowIds.length > 0
      ? `order-window-id=${filters.orderWindowIds[0].id}`
      : null;
  const programId =
    [
      "reports-pre-order-user-report",
      "reports-product-list-report",
      "reports-beacon-location-report",
    ].includes(filters.filterType) && filters.programId
      ? `filter[program-id]=${filters.programId.id}`
      : null;
  const promotionIds =
    filters.promotionIds && filters.promotionIds.length > 0
      ? separateByComma("promotion-ids", filters.promotionIds, "id")
      : null;
  const promotionApprovedAtRange =
    filters.dateRangeStart &&
    filters.dateRangeEnd &&
    filters.filterType === "reports-promo-code-report"
      ? `filter[approved-at-range]=${filters.dateRangeStart} - ${filters.dateRangeEnd}`
      : null;
  const promotionRedemptionType =
    filters.filterType === "promotion" && filters.promotionRedemptionType
      ? `filter[redemption-type]=${filters.promotionRedemptionType}`
      : null;
  const promotionStatus =
    filters.filterType === "promotion" && filters.promotionStatus
      ? `filter[is_active]=${
          filters.promotionStatus === "active" ? "true" : "false"
        }`
      : null;
  const promotionType =
    filters.filterType === "promotion" && filters.promotionType
      ? `filter[type]=${filters.promotionType}`
      : null;
  const programIds =
    filters.programIds && filters.programIds.length > 0
      ? separateByComma("program-ids", filters.programIds, "id")
      : null;
  const purchaseOrderType =
    filters.filterType === "purchase-order-history" &&
    filters.orderType &&
    filters.orderType.length > 0
      ? `filter[type]=${filters.orderType}`
      : null;
  const purchaserIds =
    filters.purchaserIds && filters.purchaserIds.length > 0
      ? separateByComma("purchaser-ids", filters.purchaserIds, "id")
      : null;
  const roles =
    filters.roles && filters.roles.length > 0
      ? separateByComma("roles", filters.roles)
      : null;
  const rollupType =
    filters.orderType && filters.filterType === "purchase-order-rollup"
      ? `filter[order-set-type]=${filters.orderType}`
      : null;
  const shippedOrdersDateRange =
    filters.filterType === "reports-shipped-orders-report" &&
    filters.dateRangeStart &&
    filters.dateRangeEnd
      ? `filter[submitted-at-range]=${filters.dateRangeStart} - ${filters.dateRangeEnd}`
      : null;
  const sku =
    filters.sku && filters.sku.length > 0 ? `filter[sku]=${filters.sku}` : null;
  const search =
    filters.search && filters.search.length > 0
      ? `filter[search]=${filters.search}`
      : null;
  const stateIds =
    filters.stateIds && filters.stateIds.length > 0
      ? separateByComma("state-ids", filters.stateIds, "id")
      : null;
  const status = handleStatus(filters);
  const supplierIds =
    filters.supplierIds && filters.supplierIds.length > 0
      ? separateByComma("supplier-ids", filters.supplierIds, "id")
      : null;
  const territoryIds =
    filters.territoryIds && filters.territoryIds.length > 0
      ? separateByComma("territory-ids", filters.territoryIds, "id")
      : null;
  const userIds =
    filters.userIds && filters.userIds.length > 0
      ? separateByComma("user-ids", filters.userIds, "id")
      : null;
  const warehouse =
    filters.warehouse && filters.warehouse.length > 0
      ? `filter[warehouse]=${filters.warehouse}`
      : null;
  const inStock =
    filters.inStock === "true" ? `filter[has-inventory]=true` : "";
  const itemVisibilityCaseString = filters.itemVisibilityCase
    ? `filter[${filters.itemVisibilityCase}]=true`
    : "";
  const dateRangeString = filters.dateRange
    ? `filter[date-range]=${filters.dateRange}`
    : "";

  const brandIds =
    filters.brandIds && filters.brandIds.length > 0
      ? separateByComma("brand-ids", filters.brandIds, "id")
      : null;

  const queryArray = [
    uniqueFilter,
    budgetGroupIds,
    budgetId,
    budgetIsActive,
    channelId,
    channelIds,
    currentTerritoryId,
    description,
    favoriteItems,
    groupIds,
    groupedBy,
    isActive,
    isOrderable,
    isVisible,
    name,
    orderHistoryType,
    orderId,
    orderSubmittedRange,
    orderWindowId,
    orderWindowIds,
    orderWindowTerritoryId,
    preOrderWindowId,
    promotionIds,
    promotionApprovedAtRange,
    promotionRedemptionType,
    promotionStatus,
    promotionType,
    programId,
    programIds,
    purchaserIds,
    purchaseOrderType,
    roles,
    rollupType,
    shippedOrdersDateRange,
    sku,
    search,
    status,
    stateIds,
    supplierIds,
    territoryIds,
    userIds,
    warehouse,
    inStock,
    itemVisibilityCaseString,
    sortString,
    dateRangeString,
    brandIds,
  ];

  const queryStringAppend = queryArray.filter((query) => query).join("&");
  const filterPreCursor = queryStringAppend.length !== 0 ? "?" : "";

  const queryString = urlBase + filterPreCursor + queryStringAppend;
  if (queryStringAppend.length > 0 && !filters.isAutoComplete) {
    const strippedFilters = buildQueryObject(filters);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?query=${encodeURIComponent(
        JSON.stringify(strippedFilters)
      )}`
    );
  } else if (!filters.isAutoComplete) {
    window.history.replaceState(null, "", window.location.pathname);
  }
  return queryString;
};

export const handleErrors = (err) => {
  if (err.response) {
    const errorPriority = [
      err.response.data?.error,
      err.response.data?.errors
        ?.map?.((e) => e.detail || e.title)
        ?.join?.("\n"),
      err.response.data.errors?.detail,
      err.response.errors?.detail,
      err.response.data?.message,
      err.response.data,
      "Unknown Error",
    ];
    //  return the first error that isn't falsey
    return errorPriority.reduce((a, b) => a || b, 0);
  } else {
    return "Unknown Error";
  }
};

export const buildSort = (filters) => {
  if (
    filters.sortBy &&
    filters.sortDirection &&
    filters.sortBy.length > 0 &&
    filters.sortDirection.length > 0
  ) {
    return `sort=${filters.sortDirection === "desc" ? "-" : ""}${
      filters.sortBy
    }`;
  } else return "";
};
